import { default as videojs } from 'video.js';
import qualityLevels, { QualityLevelList } from 'videojs-contrib-quality-levels';
import 'videojs-xr';
import { Size } from '../../analytics';
import { Environment } from '../../environment';
import {
    IPlaybackStats,
    IQualityLevel,
    PlanetMediaPlayer,
    PlanetPlayingArgs,
    PlanetTimeEventArgs,
    PlaybackSessionState,
    PlayerErrorArgs,
    PlayerFullscreenArgs,
    PlayerPositionArgs,
    PlayerTextTrackCueChangeArgs,
} from '../../media-player';
import {
    MediaItem,
    MetadataType,
    MetaEventArgs,
    OnXhrOptions,
    PlanetMediaPlayerEvents,
    PlanetMediaPlayerSetup,
    StreamingMediaSource,
    VolumeEventArgs,
} from '../../models';
import { PluginConfiguration, QualitySelectionRepresentation, VideoJsXr } from '../../plugin';
import { canAccessLocalStorage } from '../../utils';
import { Logo, Overlay, TitleBar, VideoDescription, VideoTitle } from './components';
import { CinematicViewToggle } from './components/cinematic-view-toggle';
import { QualityMenuButton } from './components/quality-menu-button';
import { VideoJsEvents, VideoJsHlsEvents } from './enums';
import { itStrings } from './it-strings';
import { PlanetVideoJs, PlanetVideoJsOptions, PlanetVideoJsTech, ProgressEvent } from './models';
import { PlaylistMapper } from './playlist-mapper';
import { VhsHandler } from './vhs-handler';

const isDesktopSafari: boolean = Environment.OS.mac && Environment.Browser.safari;
const isMobileSafari: boolean = Environment.OS.iOS && Environment.Browser.safari;
const isAppleDevice: boolean = Environment.OS.mac || Environment.OS.iPad || Environment.OS.iPhone;
const isChrome: boolean = Environment.Browser.chrome;

const isNotIE: boolean = !Environment.Browser.ie && !Environment.Browser.msie;
const supportsObjectFit: boolean = isNotIE;
const supportsVr: boolean = isNotIE;

export class VideoJsAdapter extends PlanetMediaPlayer {

    adapter: PlanetVideoJs;

    private vjsOptions: PlanetVideoJsOptions;

    private overrideNative: boolean;
    private useNativeTextTracks: boolean;
    private useNativeVideoTracks: boolean;
    private useNativeAudioTracks: boolean;

    private cuesTrack: TextTrack;
    private cuesLength: number = 0;

    private xr: VideoJsXr;

    private readonly isMobile: boolean = videojs.browser.IS_ANDROID || videojs.browser.IS_IOS;

    constructor(containerId: string, plugins: PluginConfiguration[] = []) {
        super(containerId, plugins);
    }

    /**
     *
     * @param setup
     */
    async setup(setup: PlanetMediaPlayerSetup): Promise<void> {
        /**
         * Suggested value
         * Since MSE playback may be desirable on all browsers with some native support other than Safari,
         * overrideNative: !videojs.browser.IS_SAFARI could be use
         * https://github.com/videojs/http-streaming#overridenative
         */
        const overrideNative: boolean = !videojs.browser.IS_SAFARI || setup.overrideNative === true;
        setup.overrideNative = overrideNative;
        this.overrideNative = overrideNative;
        this.useNativeTextTracks = !overrideNative || setup.useNativeTextTracks;
        this.useNativeVideoTracks = !overrideNative || setup.useNativeVideoTracks;
        this.useNativeAudioTracks = !overrideNative || setup.useNativeAudioTracks;

        await super.setup(setup);

        this.vjsOptions = this.mapPlayerOptions(setup);

        this.createPlayerElement(setup);

        this.adapter = videojs(this.getHtmlElementId(), this.vjsOptions);
        this.addItLanguage();

        (<Function>qualityLevels).bind(this.adapter)();

        // if (overrideNative) {
        //     planetQualitySelector.bind(this.adapter)();
        // }

        this.createDom();

        this.adapter.ready(() => {
            this.adapter.on('keydown', (e: KeyboardEvent) => {
                this.emit(PlanetMediaPlayerEvents.KeyDown, {
                    type: 'keydown',
                    keyCode: e.key,
                    modifiers: {
                        ctrl: e.ctrlKey,
                        alt: e.altKey,
                        shift: e.shiftKey,
                        meta: e.metaKey,
                    },
                });
            });
            this.adapter.on('keyup', (e: KeyboardEvent) => {
                const eventData = {
                    type: 'keyup',
                    keyCode: e.key,
                    modifiers: {
                        ctrl: e.ctrlKey,
                        alt: e.altKey,
                        shift: e.shiftKey,
                        meta: e.metaKey,
                    },
                };
                this.emit(PlanetMediaPlayerEvents.KeyUp, eventData);
                this.emit(PlanetMediaPlayerEvents.UserInteraction, {
                    ...eventData,
                    type: 'keyboard',
                });
            });
            this.adapter.on('dblclick', (e: MouseEvent) => {
                this.emit(PlanetMediaPlayerEvents.UserInteraction, {type: 'dblclick'});
                if (this.isMobile) {
                    const overlay: Overlay = <Overlay>this.adapter.getChild(Overlay.ComponentName);
                    if (overlay) {
                        overlay.handleDoubleClick(e);
                    }
                }
            });
            this.adapter.on('click', (e: MouseEvent) => this.emit(PlanetMediaPlayerEvents.UserInteraction, {type: 'click'}));
            this.adapter.on('event', (e) => {
                console.log('event', e);
            });

            this.adapter.on(VideoJsEvents.Progress, (e: Event) => {
                const payload: ProgressEvent = {
                    type: PlanetMediaPlayerEvents.BufferChange,
                    duration: this.getDuration(),
                    position: this.getPosition(),
                    percentile: this.adapter.bufferedPercent() * 100,
                };
                this.emit(PlanetMediaPlayerEvents.BufferChange, payload);
            });
            this.adapter.on(VideoJsEvents.Abort, (e: Event, data) => this.emit('abort', data));
            this.adapter.on(VideoJsEvents.Suspend, (e: Event, data) => this.emit('suspend', data));
            this.adapter.on(VideoJsEvents.Emptied, (e: Event, data) => this.emit('emptied', data));
            this.adapter.on(VideoJsEvents.Stalled, (e: Event, data) => this.emit('stalled', data));
            this.adapter.on(VideoJsEvents.LoadedMetadata, (e: Event) => {
                const ranges: TimeRanges = this.adapter.seekable();
                let seekRange: { start?: number, end?: number } = {};
                if (ranges.length > 0) {
                    seekRange = {
                        start: ranges.start(0),
                        end: ranges.end(0),
                    };
                }
                this.emit(PlanetMediaPlayerEvents.Meta, {
                    metadataType: MetadataType.Media,
                    width: this.adapter.videoWidth(),
                    height: this.adapter.videoHeight(),
                    duration: this.adapter.duration(),
                    seekRange,
                });

                const vhs: VhsHandler = this.adapter.tech('_')?.vhs;

                if (vhs) {
                    this.emit(PlanetMediaPlayerEvents.Levels, {
                        levels: vhs.representations(),
                        width: this.adapter.width(),
                    });
                } else {
                    console.warn('native implementation');
                }
            });
            this.adapter.on(VideoJsEvents.LoadedData, () => {
                this.emit('loadeddata');
                this.emit(PlanetMediaPlayerEvents.FirstFrame);
            });
            this.adapter.on(VideoJsEvents.TimeUpdate, () => {
                const ranges: TimeRanges = this.adapter.seekable();
                if (ranges.length > 0) {
                    const start: number = ranges.start(0);
                    const end: number = ranges.end(0);
                    const args: PlanetTimeEventArgs = {
                        type: 'time',
                        position: this.getPosition(),
                        duration: end - start,
                        viewable: this.isVisible() ? 1 : 0,
                        seekable: {start, end},
                    };
                    this.emit(PlanetMediaPlayerEvents.Time, args);
                }
            });
            this.adapter.on(VideoJsEvents.Resize, () => this.emit(PlanetMediaPlayerEvents.Resize));
            this.adapter.on(VideoJsEvents.VolumeChange, (e: VolumeEventArgs, data) => {
                this.emit(PlanetMediaPlayerEvents.Volume, {
                    volume: data,
                });
            });
            this.adapter.on(VideoJsEvents.TextTrackChange, (e) => {
                this.emit(VideoJsEvents.TextTrackChange, {
                    sourceEvent: e,
                    tracksList: this.adapter.textTracks(),
                });
            });

            this.adapter.on(VideoJsEvents.LoadStart, () => this.emit('loadstart'));
            this.adapter.on(VideoJsEvents.SourceSet, (e: Event, data) => this.emit('sourceset', data));
            this.adapter.on(VideoJsEvents.Play, () => this.emit(PlanetMediaPlayerEvents.Play));
            this.adapter.on(VideoJsEvents.RateChange, (e: Event, data) => this.emit('ratechange', data));

            this.adapter.on(VideoJsEvents.Waiting, () => {
                this.emit(PlanetMediaPlayerEvents.Buffer);
                if (isMobileSafari) {
                    // Safari triggers events in the wrong order (start --> seeked)
                    this.adapter.one(VideoJsEvents.TimeUpdate, () => {
                        const position: number = this.getPosition();
                        const event: PlayerPositionArgs = {
                            position: position,
                        };
                        if (!this.adapter.paused()) {
                            this.emit(PlanetMediaPlayerEvents.Seeked, event);
                            this.emit(PlanetMediaPlayerEvents.Playing, event);
                        } else {
                            this.emit(PlanetMediaPlayerEvents.Seeked, event);
                        }
                    });
                }
            });
            this.adapter.on(VideoJsEvents.CanPlay, () => this.emit(PlanetMediaPlayerEvents.CanPlay));
            this.adapter.on(VideoJsEvents.CanPlayThrough, () => this.emit('canplaythrough'));
            this.adapter.on(VideoJsEvents.Playing, () => {
                const event: PlanetPlayingArgs = {
                    position: this.getPosition(),
                    duration: this.getDuration(),
                };
                this.emit(PlanetMediaPlayerEvents.Playing, event);
            });
            this.adapter.on(VideoJsEvents.Seeking, () => this.emit(PlanetMediaPlayerEvents.Seeking));
            this.adapter.on(VideoJsEvents.Seeked, () => {
                if (!isMobileSafari) {
                    const event: PlayerPositionArgs = {
                        position: this.getPosition(),
                    };
                    this.emit(PlanetMediaPlayerEvents.Seeked, event);
                }
            });
            this.adapter.on(VideoJsEvents.FirstPlay, () => {
                this.emit(PlanetMediaPlayerEvents.FirstPlay);
                this.adapter.focus();
                this._started = true;
            });
            this.adapter.on(VideoJsEvents.Pause, () => this.emit(PlanetMediaPlayerEvents.Pause));
            this.adapter.on(VideoJsEvents.Ended, (e: Event, data) => this.emit(PlanetMediaPlayerEvents.Complete, data));
            this.adapter.on(VideoJsEvents.FullScreenChange, () => this.emit('fullscreenchange'));
            this.adapter.on(VideoJsEvents.TextData, (e: Event, data) => this.emit('textdata', data));
            this.adapter.on(VideoJsEvents.DurationChange, () => this.emit('durationchange'));
            this.adapter.on(VideoJsEvents.EnterFullWindow, (e: Event, data) => {
                const event: PlayerFullscreenArgs = {
                    fullscreen: true,
                };
                this.emit(PlanetMediaPlayerEvents.Fullscreen, event);
                this.emit('enterFullWindow', data);
            });
            this.adapter.on(VideoJsEvents.ExitFullWindow, (e: Event, data) => {
                const event: PlayerFullscreenArgs = {
                    fullscreen: false,
                };
                this.emit(PlanetMediaPlayerEvents.Fullscreen, event);
                this.emit('exitFullWindow', data);
            });
            this.adapter.on(VideoJsEvents.PosterChange, (e: Event, data) => this.emit(VideoJsEvents.PosterChange, data));
            this.adapter.on(VideoJsEvents.ControlsEnabled, (e: Event, data) => this.emit(VideoJsEvents.ControlsEnabled, data));
            this.adapter.on(VideoJsEvents.ControlsDisabled, (e: Event, data) => this.emit(VideoJsEvents.ControlsDisabled, data));
            this.adapter.on(VideoJsEvents.UsingNativeControls, (e: Event, data) => this.emit(VideoJsEvents.UsingNativeControls, data));
            this.adapter.on(VideoJsEvents.UsingCustomControls, (e: Event, data) => this.emit(VideoJsEvents.UsingCustomControls, data));
            this.adapter.on(VideoJsEvents.UserActive, () => this.emit(VideoJsEvents.UserActive));
            this.adapter.on(VideoJsEvents.UserInactive, () => this.emit(VideoJsEvents.UserInactive));
            this.adapter.on(VideoJsEvents.QualityChange, (e: Event, data) => this.emit(PlanetMediaPlayerEvents.VisualQuality, e, data));
            this.adapter.on(VideoJsEvents.Error, () => {
                const error: MediaError = this.adapter.error();
                const event: PlayerErrorArgs = {
                    code: error.code,
                    message: error.message,
                };
                this.emit(PlanetMediaPlayerEvents.Error, event);
            });

            this.adapter.on(VideoJsEvents.Meta, (e) => {
                this.emit(PlanetMediaPlayerEvents.Meta, e);
            });

            if (this.overrideNative) {
                // this.adapter.on('loadedmetadata', () => {
                //     const tracks = this.adapter.textTracks();
                //
                //     for (let i = 0; tracks.length > i; i++) {
                //         console.log(tracks[i]);
                //     }
                //
                // });
                this.adapter.textTracks().addEventListener('addtrack', (e: TrackEvent) => {
                    const track: TextTrack = e.track;

                    this.emit(PlanetMediaPlayerEvents.TextTrackAdded, {
                        track: track,
                    });

                    // @ts-ignore
                    const tracksLength = e?.target?.length ?? 0;
                    for (let i = 0; i < tracksLength; i++) {
                        // @ts-ignore
                        const el = e.target[i];
                        // Is hls id3 track
                        if ((el.label === 'Timed Metadata' || el.label === 'segment-metadata' || el.inBandMetadataTrackDispatchType === '15') && el.kind === 'metadata') {
                            el.mode = 'hidden';
                            this.cuesTrack = el;
                        }
                    }
                    // Hls id3 metadata
                    track.addEventListener(PlanetMediaPlayerEvents.CueChange, () => {
                        const length = this.cuesTrack?.cues?.length;
                        const cue: TextTrackCue = this.cuesTrack?.cues[length - 1];
                        // @ts-ignore
                        if (cue) {
                            this.handleId3CueChange(this.cuesTrack?.cues);
                        }
                    });

                    track.addEventListener(PlanetMediaPlayerEvents.TextTrackCueChange, () => {
                        const eventData: PlayerTextTrackCueChangeArgs = {
                            track: track,
                            activeCues: Array.prototype.slice.call(track.activeCues, 0),
                        };
                        this.emit(PlanetMediaPlayerEvents.TextTrackCueChange, eventData);
                    });
                });
            } else {
                const vtt: TextTrackList = this.adapter.textTracks();
                let id3HandlerSet: boolean = false;
                vtt.addEventListener('addtrack', (e: TrackEvent) => {
                    const track: TextTrack = e.track;

                    this.emit(PlanetMediaPlayerEvents.TextTrackAdded, {
                        track: track,
                    });

                    for (let i = 0; i < vtt.length; i++) {
                        const label: string = vtt[i].label;
                        const kind: TextTrackKind = <TextTrackKind>vtt[i].kind;

                        // Hls id3 metadata
                        if (!id3HandlerSet && (label === 'Timed Metadata' || label === 'segment-metadata' || label === '') && kind === 'metadata') {
                            vtt[i].mode = 'hidden';
                            vtt[i].addEventListener(PlanetMediaPlayerEvents.CueChange, () => {
                                this.handleId3CueChange(vtt[i].cues);

                            });
                            id3HandlerSet = true;
                        }
                    }
                });
            }

            this.emit(PlanetMediaPlayerEvents.Setup, {
                config: this.getConfig(),
                envSettings: {
                    overrideNative: this.overrideNative,
                    supportsVr: supportsVr,
                    useNativeTextTracks: this.useNativeTextTracks,
                    useNativeAudioTracks: this.useNativeAudioTracks,
                    useNativeVideoTracks: this.useNativeVideoTracks,
                },
            });

            this.load(setup.playlist)
                .catch((error: Error) => {
                    console.log('error while loading the playlist');
                    this.emit(PlanetMediaPlayerEvents.SetupError, {
                        message: 'The playlist could not be loaded',
                        name: 4,
                        playlist: setup.playlist,
                        sourceError: error,
                    });
                }).then(() => {
                const tech: PlanetVideoJsTech = this.adapter.tech('_');
                tech.on(VideoJsHlsEvents.RetryPlaylist, (event: Event) => {
                    this.emit(PlanetMediaPlayerEvents.HlsManifestLoadRetry, {
                        sourceEvent: event,
                        playlist: tech?.vhs.playlists,
                    });
                });
            });
        });
    }

    private addItLanguage(): void {
        videojs.addLanguage('it', itStrings);
    }

    private handleId3CueChange(cues: TextTrackCueList): void {
        const length = cues?.length;
        let cue: VTTCue;
        const offset: number = this.adapter.tech('_')?.sourceHandler_?.mediaSource?.activeSourceBuffers[0]?.timestampOffset;
        for (let i = this.cuesLength; i < length; i++) {
            cue = <VTTCue>cues[i];
            const timeout: number = (cue.startTime + offset - this.adapter.currentTime()) * 1000;
            this.emitId3Cue(cue, timeout);
        }

        this.cuesLength = length;
    }

    private emitId3Cue(cue: VTTCue, timeout: number): void {
        setTimeout(() => {
            this.emit(PlanetMediaPlayerEvents.MetadataCueChange, cue);
        }, timeout);
    }

    /**
     *
     * @param item
     */
    async setPlaylistItem(item: MediaItem): Promise<MediaItem> {

        if (this.isRemoved) {
            return new Promise((resolve) => resolve(null));
        }

        return new Promise(async (resolve, reject) => {

            /**
             * Resolve the promise and stop listening for other events.
             */
            const doResolve = () => {
                this.adapter.off(VideoJsEvents.Error, onError);
                this.adapter.off(VideoJsEvents.LoadStart, onLoadStart);
                this.adapter.off(VideoJsEvents.LoadedMetadata, onLoadedMetadata);
                this.adapter.off(VideoJsEvents.DurationChange, onDurationChange);
                this.adapter.off(VideoJsEvents.SourceSet, onSourceSet);
                resolve(item);
            };

            /**
             * Reject the promise if there are errors while loading the playlist/playlist-item
             */
            const onError = (e) => {
                this.adapter.off(VideoJsEvents.LoadStart, onLoadStart);
                this.adapter.off(VideoJsEvents.LoadedMetadata, onLoadedMetadata);
                this.adapter.off(VideoJsEvents.DurationChange, onDurationChange);
                this.adapter.off(VideoJsEvents.SourceSet, onSourceSet);
                reject(e);
            };

            const onLoadStart = () => {
                const tech: PlanetVideoJsTech = this.adapter.tech('_');
                /**
                 * HlsHandler
                 */
                if (tech.vhs) {
                    tech.vhs.xhr.beforeRequest = (request: OnXhrOptions) => {
                        request.headers = request.headers || {};

                        this.emit(VideoJsEvents.VHS_BeforeSegmentRequest, request);

                        if (typeof this.config.onXhr === 'function') {
                            this.config.onXhr(request);
                        }

                        const item: MediaItem = this.getCurrentPlaylistItem();

                        // allow xhr transforms
                        this.emit(PlanetMediaPlayerEvents.BeforeXhrRequest, {
                            request: request,
                            item: item,
                        });

                        return request;
                    };

                    tech.vhs.on(VideoJsHlsEvents.RetryPlaylist, (e: Event) => {
                        console.log('hls playlist reload');
                        this.emit(PlanetMediaPlayerEvents.HlsManifestLoadRetry, {
                            sourceEvent: e,
                            playlists: tech.vhs.playlists,
                        });
                    });
                }

            };

            /**
             * Resolve the promise when sourceSet is triggered
             */
            const onSourceSet = () => {
                doResolve();
            };

            /**
             * Wait for data to be loaded before resolving the promise.
             * This way we know the player can actually play the content
             * before triggering "onPlaylistItem"
             */
            const onLoadedMetadata = () => {
                doResolve();
            };

            const onLoadedData = () => {
                const tech: PlanetVideoJsTech = this.adapter.tech('_');
                /**
                 * HlsHandler
                 */
                if (!tech.vhs) {
                    console.warn('no tech yet');
                }

                const vhs: VhsHandler = tech.vhs;
                if (vhs) {
                    const list: QualitySelectionRepresentation[] = vhs.representations();
                    let toggle: QualityMenuButton = this.adapter.controlBar.getChild(QualityMenuButton.ComponentName) as QualityMenuButton;
                    if (toggle) {
                        this.adapter.controlBar.removeChild(toggle);
                    }
                    if (list) {
                        let index: number = this.adapter.controlBar.children().findIndex(c => c.name() === 'PictureInPictureToggle');
                        if (index > -1) {
                            index++;
                        } else {
                            index = this.adapter.controlBar.children().length - 1;
                        }
                        this.adapter.controlBar.addChild(QualityMenuButton.ComponentName, {}, index);
                    }
                }

                const {width, height} = this.getCurrentDimensions();
                const cinematicView: CinematicViewToggle = this.adapter.controlBar?.getChild(CinematicViewToggle.ComponentName) as CinematicViewToggle;
                // If vertical video, hide cinematic view toggle
                if (width < height) {
                    if (cinematicView) {
                        cinematicView.toggle(false);
                        cinematicView.hide();
                        this.emit(PlanetMediaPlayerEvents.CinematicViewToggle, cinematicView.isCinematicView());
                    }
                } else {
                    if (cinematicView) {
                        cinematicView.show();
                    }
                }
            };

            /**
             *
             * Live streams never fire the `canplaythrough`event. We wait for
             * the duration change event, which is emitted when the player parsed
             * the manifest in http streams
             */
            const onDurationChange = () => {
                if (this.getDuration() === Infinity) {
                    doResolve();
                }
            };

            this.adapter.pause();
            this.adapter.reset();
            this.adapter.one(VideoJsEvents.Error, onError);
            this.adapter.one(VideoJsEvents.LoadedMetadata, onLoadedMetadata);
            this.adapter.one(VideoJsEvents.LoadStart, onLoadStart);
            this.adapter.one(VideoJsEvents.LoadedData, onLoadedData);
            this.adapter.one(VideoJsEvents.DurationChange, onDurationChange);
            this.adapter.one(VideoJsEvents.SourceSet, onSourceSet);

            if (this.getConfig().limitRenditionByPlayerDimensions !== undefined
                && item.limitRenditionByPlayerDimensions === undefined) {
                item.limitRenditionByPlayerDimensions = this.getConfig().limitRenditionByPlayerDimensions;
            } else {
                item.limitRenditionByPlayerDimensions = true;
            }

            const sources: videojs.Tech.SourceObject[] = PlaylistMapper.mapMediaItem(item);

            if (supportsVr) {
                if (this.adapter && item.panoramic === true) {
                    this.xr = this.adapter.xr();
                } else {
                    if (this.xr && this.xr.player) {
                        this.xr.dispose();
                        this.removeClass('vjs-xr');
                    }
                }
            }

            this.adapter.src(sources);
            this.adapter.preload(true);

            if (item.image) {
                this.adapter.poster(item.image);
            }

            if (item.tracks) {
                const tracks: videojs.TextTrackOptions[] = PlaylistMapper.mapVideoTextTracks(item.tracks);
                for (let track of tracks) {
                    const remoteTextTrack: HTMLTrackElement = this.adapter.addRemoteTextTrack(track, false);
                    remoteTextTrack.addEventListener('load', (e) => {
                        this.emit(PlanetMediaPlayerEvents.TextTrackLoaded, {
                            track: remoteTextTrack.track,
                        });
                    });
                }
            }

            this.update(item);
        });
    }

    setPoster(image: string): void {
        this.adapter?.poster(image);
    }

    setTitle(title: string): void {
        const overlay: Overlay = <Overlay>this.adapter.getChild(Overlay.ComponentName);
        const titleBar: TitleBar = <TitleBar>overlay?.getChild(TitleBar.ComponentName);
        if (titleBar) {
            titleBar.updateTextContent({
                title: title,
            });
        }
    }

    setDescription(description: string): void {
        const overlay: Overlay = <Overlay>this.adapter.getChild(Overlay.ComponentName);
        const titleBar: TitleBar = <TitleBar>overlay?.getChild(TitleBar.ComponentName);
        if (titleBar) {
            titleBar.updateTextContent({
                description: description,
            });
        }
    }

    /**
     * Returns the container HTML element
     */
    getContainer(): HTMLElement {
        return document.getElementById(this.containerId);
    }

    pause(): void {
        if (this.isRemoved) {
            return;
        }
        this.adapter.pause();
    }

    getPlaybackQuality(): VideoPlaybackQuality {
        return this.adapter.getVideoPlaybackQuality();
    }

    /**
     * Returns IPlaybackStats
     */
    getPlaybackStats(): IPlaybackStats {
        if (this.isRemoved) {
            return {
                timestamp: 0,
                playbackSessionId: '',
                mediaRequests: 0,
                mediaRequestsAborted: 0,
                mediaRequestsErrored: 0,
                mediaRequestsTimedOut: 0,
                mediaTransferDuration: 0,
                mediaBytesTransferred: 0,
                mediaSecondsLoaded: 0,
                currentSource: {
                    type: 'hls',
                    uri: '',
                },
            };
        }

        const session: PlaybackSessionState = this.getPlaybackSession();

        if (!session) {
            return null;
        }

        const vhs: VhsHandler = this.adapter.tech('_')?.vhs;
        if (vhs) {
            const stats: IPlaybackStats = {
                timestamp: vhs.stats?.timestamp,
                playbackSessionId: session.id,
                mediaRequests: vhs.stats?.mediaRequests,
                mediaRequestsAborted: vhs.stats?.mediaRequestsAborted,
                mediaRequestsErrored: vhs.stats?.mediaRequestsErrored,
                mediaRequestsTimedOut: vhs.stats?.mediaRequestsTimedOut,
                mediaTransferDuration: vhs.stats?.mediaTransferDuration,
                mediaBytesTransferred: vhs.stats?.mediaBytesTransferred,
                mediaSecondsLoaded: Number(vhs.stats?.mediaSecondsLoaded.toFixed(3)),
                currentSource: {
                    type: vhs.stats?.currentSource?.type,
                    uri: vhs.stats?.currentSource?.src,
                },
            };

            const levels: QualityLevelList = this.adapter?.qualityLevels();
            if (levels) {
                const rendition: IQualityLevel = levels[levels.selectedIndex];
                if (rendition) {
                    stats.currentRendition = {
                        width: rendition.width,
                        height: rendition.height,
                        bitrate: rendition.bitrate,
                        id: rendition.id,
                    };
                }
            }

            return stats;
        }
        return null;
    }

    /**
     *
     */
    play(): Promise<void> {
        if (this.isRemoved) {
            return;
        }
        return this.adapter.play();
    }

    /**
     *
     */
    getMediaMetadata(): Size {
        if (this.isRemoved) {
            return {width: 0, height: 0};
        }
        return {
            width: this.adapter.videoWidth(),
            height: this.adapter.videoHeight(),
        };
    }

    /**
     *
     */
    setVolume(number: number): void {
        if (this.isRemoved) {
            return;
        }
        this.adapter.volume(number);
    }

    /**
     *
     */
    getVolume(): number {
        if (this.isRemoved) {
            return 0;
        }
        return this.adapter.volume();
    }

    /**
     *
     */
    getDuration(): number {
        if (this.isRemoved) {
            return 0;
        }
        return this.adapter.duration();
    }

    /**
     *
     */
    getPosition(): number {
        if (this.isRemoved) {
            return 0;
        }
        return this.adapter.currentTime();
    }

    /**
     *
     * @param time
     */
    seek(time: number): void {
        if (this.isRemoved) {
            return;
        }
        return this.adapter.currentTime(time);
    }

    getQualityLevels(): IQualityLevel[] {
        return [];
    }

    /**
     * @param muted
     */
    setMuted(muted: boolean): void {
        if (this.isRemoved) {
            return;
        }
        this.adapter.muted(muted);
    }

    /**
     *
     */
    getMuted(): boolean {
        if (this.isRemoved) {
            return false;
        }
        return this.adapter.muted();
    }

    /**
     * Off, pause, reset and dispose (if true)
     */
    remove(dispose: boolean = true): HTMLElement {
        super.remove();
        const container: HTMLElement = this.getContainer();
        try {
            this.adapter.off();
            this.adapter.pause();
            this.adapter.reset();
            if (dispose) {
                this.adapter.dispose();
            } else {
                this.adapter.reset();
            }
        } catch (e) {
            this.adapter = null;
        }

        return container;
    }

    /**
     * Get Api configuration
     */
    getApiConfig(): PlanetVideoJsOptions {
        return this.vjsOptions;
    }

    /**
     *
     */
    getCurrentSource(): StreamingMediaSource {
        return null;
    }

    /**
     * Get if it's player is currently in fullscreen
     */
    getFullscreen(): boolean {
        if (this.isRemoved) {
            return false;
        }
        if (this.adapter.supportsFullScreen()) {
            return this.adapter.isFullscreen();
        }
    }

    /**
     * Get MetaEventArgs
     */
    getMeta(): MetaEventArgs {
        return this.metadata;
        // if (this.isRemoved) {
        //     return true;
        // }
        // return {};
    }

    /**
     * Get current player provider
     * videojs
     */
    getProvider(): string {
        return 'videojs';
    }

    /**
     *
     */
    setControls(bool: boolean): boolean | videojs.Tech {
        if (this.isRemoved) {
            return false;
        }
        return this.adapter.tech('_');
    }

    /**
     *
     * @param fullscreen
     */
    setFullscreen(fullscreen: boolean): boolean {
        if (this.isRemoved) {
            return false;
        }
        // @ts-ignore
        const supportsFullscreen: boolean = document.fullscreenEnabled || document.webkitFullscreenEnabled;

        const enterFullscreen = supportsFullscreen ?
            this.adapter.requestFullscreen.bind(this.adapter) :
            this.adapter.enterFullWindow.bind(this.adapter);

        const exitFullscreen = supportsFullscreen ?
            this.adapter.exitFullscreen.bind(this.adapter) :
            this.adapter.exitFullWindow.bind(this.adapter);

        if (fullscreen) {
            enterFullscreen();
            return true;
        } else {
            exitFullscreen();
            return false;
        }
    }

    setPlaybackRate(rate: number): void {
        this.adapter.playbackRate(rate);
    }

    getPlaybackRate(): number {
        return this.adapter.playbackRate();
    }

    getTextTracks(): TextTrackList {
        return this.adapter.textTracks();
    }

    stop(): void {
        if (this.isRemoved) {
            return;
        }
        try {
            this.adapter.reset();
            this._started = false;
        } catch (e) {
            console.warn(e);
        }
    }

    onDisplayClick(): void {
        if (!this.isMobile && this.adapter.hasStarted()) {
            const overlay: Overlay = <Overlay>this.adapter.getChild(Overlay.ComponentName);
            if (overlay) {
                overlay.togglePlayback(!this.adapter.paused());
                this.togglePlayback();
            }
        }
    }

    onDisplayDoubleClick(): void {
        if (!this.isMobile) {
            this.toggleFullscreen();
        }
    }

    addClass(className: string): void {
        const mediaElement: HTMLElement = document.getElementById(this.getHtmlElementId());
        if (!mediaElement.classList.contains(className)) {
            mediaElement.classList.add(className);
        }
    }

    removeClass(className: string): void {
        const mediaElement: HTMLElement = document.getElementById(this.getHtmlElementId());
        if (mediaElement.classList.contains(className)) {
            mediaElement.classList.remove(className);
        }
    }

    addTextTrack(options: videojs.TextTrackOptions, manualCleanup: boolean = true): void {
        this.adapter.addRemoteTextTrack(options, manualCleanup);
    }

    getCurrentDimensions(): { width?: number, height?: number } {
        return this.adapter.currentDimensions();
    }

    toggleCinematicView(enabled: boolean): void {
        const cinematicView: CinematicViewToggle = this.adapter.controlBar.getChild(CinematicViewToggle.ComponentName) as CinematicViewToggle;
        if (cinematicView) {
            cinematicView.toggle(enabled);
        }
    }

    getCinematicViewToggle(): CinematicViewToggle {
        return this.adapter.controlBar?.getChild(CinematicViewToggle.ComponentName) as CinematicViewToggle;
    }

    aspectRatio(aspectRatio: string): void {
        this.adapter.aspectRatio(aspectRatio);
    }

    fill(enabled: boolean): void {
        this.adapter.fill(enabled);
    }

    fluid(enabled: boolean): void {
        this.adapter.fluid(enabled);
    }

    async load(playlist: MediaItem[] | MediaItem | string): Promise<MediaItem[]> {
        const audioOnly: boolean = Array.isArray(playlist) ? (<MediaItem[]>playlist)[0]?.audioOnly : (<MediaItem>playlist)?.audioOnly;
        this.checkAudioClass(audioOnly);
        return await super.load(playlist);
    }

    /**
     *
     * @param setup PlanetMediaPlayerSetup
     */
    private mapPlayerOptions(setup: PlanetMediaPlayerSetup): PlanetVideoJsOptions {
        const options: PlanetVideoJsOptions = {
            defaultVolume: 50,
            aspectRatio: typeof setup.aspectratio === 'string' ? setup.aspectratio : undefined,
            fluid: setup.fluid,
            controls: true,
            language: setup.language ?? 'it',
            muted: setup.mute === true,
            loop: setup.repeat === true,
            techOrder: ['html5'],
            playbackRates: setup.playbackRates,
            responsive: true,
            poster: setup?.image ?? '',
            sourceOrder: true,
            playsinline: true,
            liveui: true,
            controlBar: {
                volumePanel: {
                    inline: false,
                },
            },
            html5: {
                vhs: {
                    bandwidth: 30 * 1024 * 1024,
                    useBandwidthFromLocalStorage: canAccessLocalStorage(),
                    overrideNative: setup.overrideNative,
                    enableLowInitialPlaylist: setup.enableLowInitialPlaylist === true,
                    limitRenditionByPlayerDimensions: setup.limitRenditionByPlayerDimensions === true,
                },
                nativeTextTracks: this.useNativeTextTracks,
                nativeAudioTracks: this.useNativeAudioTracks,
                nativeVideoTracks: this.useNativeVideoTracks,
            },
            ...setup?.videojs,
            userActions: {
                hotkeys: (e: videojs.KeyboardEvent) => {
                    e.preventDefault();
                    this.handleKeyEvents(e.which);
                },
            },
        };

        if (setup.aspectratio) {
            options.aspectRatio = setup.aspectratio;
            options.fluid = true;
        }

        return options;
    }

    /**
     *
     * @param setup
     * @private
     */
    private createPlayerElement(setup: PlanetMediaPlayerSetup): HTMLAudioElement | HTMLVideoElement {
        const isAudio: boolean = setup.audioOnly;
        const type: 'audio' | 'video' = isAudio ? 'audio' : 'video';
        const mediaElement: HTMLAudioElement | HTMLVideoElement = document.createElement(type);
        mediaElement.setAttribute('id', this.getHtmlElementId());
        if (isAudio) {
            mediaElement.setAttribute('class', 'video-js vjs-planet vjs-planet-audio-only');
        } else {
            mediaElement.setAttribute('class', 'video-js vjs-planet');
        }
        if (setup.classList) {
            mediaElement.classList.add(setup.classList);
        }
        const container: HTMLElement = this.getContainer();
        if (container.hasChildNodes()) {
            container.childNodes.forEach(child => container.removeChild(child));
        }
        container.appendChild(mediaElement);
        this.checkAudioClass(isAudio);
        return mediaElement;
    }

    private checkAudioClass(isAudio: boolean = false): void {
        const mediaElement: HTMLAudioElement | HTMLVideoElement = document.getElementById(this.getHtmlElementId()) as HTMLAudioElement | HTMLVideoElement;
        if (isAudio) {
            if (!mediaElement.classList.contains('vjs-planet-audio-only')) {
                mediaElement.classList.add('vjs-planet-audio-only');
            }
        } else {
            if (mediaElement.classList.contains('vjs-planet-audio-only')) {
                mediaElement.classList.remove('vjs-planet-audio-only');
            }
        }
    }

    private createDom(): void {

        // const qualityMenuButton: QualityMenuButton = new QualityMenuButton(this.adapter, {});
        // this.adapter.addChild(qualityMenuButton, {});

        const bigPlayButton: videojs.Component = this.adapter.bigPlayButton;

        const overlay: Overlay = new Overlay(this.adapter, {isMobile: this.isMobile});
        const titleBar: TitleBar = new TitleBar(this.adapter);

        if (this.config.fill) {
            this.adapter.addClass('vjs-planet-fill');
        }
        if (!this.config.displaytitle) {
            titleBar.getChild(VideoTitle.ComponentName).addClass('vjs-planet-hide');
        }
        if (!this.config.displaydescription) {
            titleBar.getChild(VideoDescription.ComponentName).addClass('vjs-planet-hide');
        }
        if (supportsObjectFit) {
            this.adapter.addClass(`vjs-planet-stretching-${this.config.stretching}`);
        }

        const index: number = this.adapter.children().indexOf(bigPlayButton);
        this.adapter.addChild(overlay, {
            name: Overlay.ComponentName,
        }, index - 1);
        overlay.addChild(titleBar);

        let dbClickTimer = null;
        let prevent = false;
        overlay.on('click', (e) => {
            if (typeof dbClickTimer === 'number') {
                clearTimeout(dbClickTimer);
                e.preventDefault();
                e.stopPropagation();
            }
            dbClickTimer = setTimeout(() => {
                if (!prevent) {
                    this.emit(PlanetMediaPlayerEvents.DisplayClick, e);
                }
                prevent = false;
            }, 200);
        });
        overlay.on('dblclick', (e) => {
            clearTimeout(dbClickTimer);
            prevent = true;
            this.emit(PlanetMediaPlayerEvents.DisplayDoubleClick, e);
            prevent = false;
        });

        this.adapter.controlBar.getChild('playToggle').on('keydown', (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                overlay.togglePlayback(!this.adapter.paused());
            }
        });

        if (this.config.logo) {
            const logo: Logo = new Logo(this.adapter, {
                ...this.config.logo,
            });
            this.adapter.addChild(logo, {
                name: Logo.ComponentName,
            }, index);
        }

        if (this.config.cinematic?.enableToggle) {
            const cinematicView: CinematicViewToggle = new CinematicViewToggle(this.adapter, this.config.cinematic);
            this.adapter.controlBar.addChild(cinematicView, {}, this.adapter.controlBar.children().length - 1);
            cinematicView.on('click', () => {
                this.emit(PlanetMediaPlayerEvents.CinematicViewToggle, cinematicView.isCinematicView());
            });
        }
    }

    /**
     *
     * @param item
     */
    private update(item: MediaItem): void {
        if (this.isRemoved || !item) {
            return;
        }

        const overlay: Overlay = <Overlay>this.adapter.getChild(Overlay.ComponentName);
        const titleBar: TitleBar = <TitleBar>overlay.getChild(TitleBar.ComponentName);
        if (titleBar) {
            titleBar.updateTextContent({
                title: item.title,
                description: item.description,
            });
        }
    }

    private toggleFullscreen(): void {
        this.setFullscreen(!this.adapter.isFullscreen());
    }

    private toggleMute(): void {
        this.setMuted(!this.getMuted());
    }

    private handleKeyEvents(key: number | string): void {
        const overlay: Overlay = <Overlay>this.adapter.getChild(Overlay.ComponentName);
        switch (key) {
            case 32: // Space
                overlay.togglePlayback(!this.adapter.paused());
                this.togglePlayback();
                // Focus play toggle because if we don't, the player get scrolled to the bottom
                this.adapter.getChild('controlBar').getChild('playToggle').focus();
                return;
            case 37: // Arrow left
                overlay.seekBackward();
                break;
            case 38: // Arrow up
                overlay.turnVolumeUp();
                break;
            case 39: // Arrow right
                overlay.seekForward();
                break;
            case 40: // Arrow down
                overlay.turnVolumeDown();
                break;
            case 70: // Key F
                this.toggleFullscreen();
                break;
            case 77: // Key M
                this.toggleMute();
                break;
        }
    }
}
